import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, Plus, Edit, Trash2, Settings, Save } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell } from '../components/AdminShared';

export default function AdminCreditsPage() {
  const [bundles, setBundles] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  const [editBundle, setEditBundle] = useState(null);
  const [bundleForm, setBundleForm] = useState({ name: '', credits: 100, price_cents: 2000, discount_percent: 0, active: true });
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [tierForm, setTierForm] = useState({ name: '', credits_per_month: 100, price_cents_per_month: 4900, features: '' });
  // Credit settings
  const [settings, setSettings] = useState({ base_rate_cents: 20, ai_credits_per_minute: 1, human_credits_per_minute: 3 });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [bRes, tRes, sRes] = await Promise.all([adminAPI.listCreditBundles(), adminAPI.listSubscriptionTiers(), adminAPI.getCreditSettings()]);
      setBundles(bRes.data); setTiers(tRes.data); setSettings(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreateBundle = () => { setEditBundle(null); setBundleForm({ name: '', credits: 100, discount_percent: 0, active: true }); setShowBundleDialog(true); };
  const openEditBundle = (b) => { setEditBundle(b); setBundleForm({ name: b.name, credits: b.credits, discount_percent: b.discount_percent || 0, active: b.active !== false }); setShowBundleDialog(true); };
  const handleBundleSubmit = async () => {
    try {
      const computedPrice = Math.round(bundleForm.credits * settings.base_rate_cents * (1 - bundleForm.discount_percent / 100));
      const payload = { ...bundleForm, price_cents: computedPrice };
      if (editBundle) await adminAPI.updateCreditBundle(editBundle.id, payload);
      else await adminAPI.createCreditBundle(payload);
      setShowBundleDialog(false); load();
    } catch (e) { console.error(e); }
  };
  const deleteBundle = async (id) => { if (window.confirm('Delete this bundle?')) { try { await adminAPI.deleteCreditBundle(id); load(); } catch (e) { console.error(e); } } };

  const openCreateTier = () => { setEditTier(null); setTierForm({ name: '', credits_per_month: 100, price_cents_per_month: 4900, features: '' }); setShowTierDialog(true); };
  const openEditTier = (t) => { setEditTier(t); setTierForm({ name: t.name, credits_per_month: t.credits_per_month, price_cents_per_month: t.price_cents_per_month, features: (t.features || []).join(', ') }); setShowTierDialog(true); };
  const handleTierSubmit = async () => {
    try {
      const payload = { ...tierForm, features: tierForm.features.split(',').map(f => f.trim()).filter(Boolean) };
      if (editTier) await adminAPI.updateSubscriptionTier(editTier.id, payload);
      else await adminAPI.createSubscriptionTier(payload);
      setShowTierDialog(false); load();
    } catch (e) { console.error(e); }
  };
  const deleteTier = async (id) => { if (window.confirm('Delete this tier?')) { try { await adminAPI.deleteSubscriptionTier(id); load(); } catch (e) { console.error(e); } } };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await adminAPI.updateCreditSettings(settings);
      setSettings(res.data);
      toast.success('Credit settings saved');
    } catch (e) { toast.error('Failed to save settings'); }
    setSavingSettings(false);
  };

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell>
      <div className="p-6 md:p-8 max-w-6xl animate-fade-in space-y-10" data-testid="admin-credits-page">
        {/* Credit Settings */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Credits & Subscriptions</h1>
              <p className="text-muted-foreground mt-1 text-sm">Manage pricing, credit bundles, discounts, and subscription tiers</p>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-5 mb-2" data-testid="credit-settings-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold" style={{ fontFamily: 'DM Sans' }}>Credit Pricing Settings</h2>
              </div>
              <Button size="sm" onClick={handleSaveSettings} disabled={savingSettings} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white gap-1" data-testid="save-credit-settings">
                {savingSettings ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Base Rate (cents per credit)</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" value={settings.base_rate_cents} onChange={e => setSettings(p => ({ ...p, base_rate_cents: parseInt(e.target.value) || 0 }))} className="font-mono" data-testid="setting-base-rate" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">= ${(settings.base_rate_cents / 100).toFixed(2)}/credit</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">AI Credits per Minute</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" value={settings.ai_credits_per_minute} onChange={e => setSettings(p => ({ ...p, ai_credits_per_minute: parseInt(e.target.value) || 1 }))} className="font-mono" data-testid="setting-ai-credits" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">= ${((settings.base_rate_cents * settings.ai_credits_per_minute) / 100).toFixed(2)}/min</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Human Credits per Minute</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" value={settings.human_credits_per_minute} onChange={e => setSettings(p => ({ ...p, human_credits_per_minute: parseInt(e.target.value) || 1 }))} className="font-mono" data-testid="setting-human-credits" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">= ${((settings.base_rate_cents * settings.human_credits_per_minute) / 100).toFixed(2)}/min</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Credit Bundles */}
        <section>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ fontFamily: 'DM Sans' }}>Credit Bundles</h2>
            <Button size="sm" onClick={openCreateBundle} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-bundle-button"><Plus className="w-4 h-4 mr-1" />Add Bundle</Button>
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Credits</TableHead><TableHead>Price</TableHead><TableHead>Discount</TableHead><TableHead>Effective Price</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {bundles.map(b => {
                  const computedPrice = Math.round(b.credits * settings.base_rate_cents * (1 - (b.discount_percent || 0) / 100));
                  return (
                    <TableRow key={b.id} data-testid={`bundle-row-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell className="font-mono">{b.credits}</TableCell>
                      <TableCell className="font-mono">
                        ${(b.credits * settings.base_rate_cents / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>{b.discount_percent > 0 ? <Badge className="text-xs bg-green-500/10 text-green-600 border-green-500/20">{b.discount_percent}% off</Badge> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="font-mono font-semibold">${(computedPrice / 100).toFixed(2)}</TableCell>
                      <TableCell><Badge variant={b.active !== false ? 'default' : 'secondary'} className="text-xs">{b.active !== false ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditBundle(b)} data-testid={`edit-bundle-${b.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteBundle(b.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {bundles.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No credit bundles</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Subscription Tiers */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ fontFamily: 'DM Sans' }}>Subscription Tiers</h2>
            <Button size="sm" onClick={openCreateTier} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-tier-button"><Plus className="w-4 h-4 mr-1" />Add Tier</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map(t => (
              <div key={t.id} className="rounded-lg border bg-card p-5 space-y-3" data-testid={`tier-card-${t.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{t.name}</h3>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditTier(t)} data-testid={`edit-tier-${t.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteTier(t.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="text-3xl font-bold font-mono">${(t.price_cents_per_month / 100).toFixed(0)}<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <p className="text-sm text-muted-foreground">{t.credits_per_month} credits/month</p>
                <ul className="space-y-1">
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[hsl(var(--brand))]" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Bundle Dialog */}
        <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
          <DialogContent data-testid="bundle-dialog">
            <DialogHeader><DialogTitle>{editBundle ? 'Edit Credit Bundle' : 'Create Credit Bundle'}</DialogTitle>
              <DialogDescription>Configure credits, pricing, and optional discounts</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Bundle Name *</Label><Input value={bundleForm.name} onChange={e => setBundleForm(p => ({ ...p, name: e.target.value }))} placeholder="Pro Pack" data-testid="bundle-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Credits *</Label><Input type="number" value={bundleForm.credits} onChange={e => setBundleForm(p => ({ ...p, credits: parseInt(e.target.value) || 0 }))} data-testid="bundle-credits" /></div>
                <div className="space-y-2"><Label>Discount %</Label><Input type="number" step="0.1" value={bundleForm.discount_percent} onChange={e => setBundleForm(p => ({ ...p, discount_percent: parseFloat(e.target.value) || 0 }))} data-testid="bundle-discount" /></div>
              </div>
              <div className="flex items-center gap-3"><Switch checked={bundleForm.active} onCheckedChange={v => setBundleForm(p => ({ ...p, active: v }))} data-testid="bundle-active" /><Label>Active</Label></div>
              <div className="rounded-md bg-muted/50 border p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Base rate</span><span className="font-mono">${(settings.base_rate_cents / 100).toFixed(2)}/token</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Full price ({bundleForm.credits} x ${(settings.base_rate_cents / 100).toFixed(2)})</span><span className="font-mono">${(bundleForm.credits * settings.base_rate_cents / 100).toFixed(2)}</span></div>
                {bundleForm.discount_percent > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount ({bundleForm.discount_percent}%)</span><span className="font-mono text-green-600">-${(bundleForm.credits * settings.base_rate_cents * bundleForm.discount_percent / 10000).toFixed(2)}</span></div>}
                <div className="flex justify-between font-semibold border-t pt-1 mt-1"><span>Published price</span><span className="font-mono">${(bundleForm.credits * settings.base_rate_cents * (1 - bundleForm.discount_percent / 100) / 100).toFixed(2)}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBundleDialog(false)}>Cancel</Button>
              <Button onClick={handleBundleSubmit} disabled={!bundleForm.name || !bundleForm.credits} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-bundle">{editBundle ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tier Dialog */}
        <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
          <DialogContent data-testid="tier-dialog">
            <DialogHeader><DialogTitle>{editTier ? 'Edit Subscription Tier' : 'Create Subscription Tier'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Tier Name *</Label><Input value={tierForm.name} onChange={e => setTierForm(p => ({ ...p, name: e.target.value }))} placeholder="Professional" data-testid="tier-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Credits/Month *</Label><Input type="number" value={tierForm.credits_per_month} onChange={e => setTierForm(p => ({ ...p, credits_per_month: parseInt(e.target.value) || 0 }))} data-testid="tier-credits" /></div>
                <div className="space-y-2"><Label>Price/Month (cents) *</Label><Input type="number" value={tierForm.price_cents_per_month} onChange={e => setTierForm(p => ({ ...p, price_cents_per_month: parseInt(e.target.value) || 0 }))} data-testid="tier-price" /></div>
              </div>
              <div className="space-y-2"><Label>Features (comma-separated)</Label><Input value={tierForm.features} onChange={e => setTierForm(p => ({ ...p, features: e.target.value }))} placeholder="100 credits/month, Priority support, ..." data-testid="tier-features" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
              <Button onClick={handleTierSubmit} disabled={!tierForm.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-tier">{editTier ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminPageShell>
  );
}
